import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orgsApi } from '@/api/organizations';
import { keysApi, type ApiKeyInfo } from '@/api/keys';
import { useAuth } from '@/contexts/AuthContext';
import { queryKeys } from '@/lib/queryKeys';
import { formatDate, formatDateTime } from '@/lib/formatDate';
import { ApiError } from '@/api/client';
import { Key, Trash2, Copy, Check } from 'lucide-react';

export function SettingsPage() {
  const { user, organizations } = useAuth();
  const orgId = user?.orgId ?? '';
  const qc = useQueryClient();
  const [tab, setTab] = useState<'org' | 'account' | 'keys'>('org');
  const [orgName, setOrgName] = useState('');
  const [orgSlug, setOrgSlug] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [newKeyValue, setNewKeyValue] = useState('');
  const [copied, setCopied] = useState(false);

  const org = organizations.find((o) => o.id === orgId);

  const { data: keysData } = useQuery({
    queryKey: ['apikeys'],
    queryFn: () => keysApi.list(),
    enabled: tab === 'keys',
  });

  const updateOrgMutation = useMutation({
    mutationFn: (input: { name?: string; slug?: string }) => orgsApi.update(orgId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.auth.me });
      setSuccess('Organizacao atualizada');
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Falha'),
  });

  const createKeyMutation = useMutation({
    mutationFn: (label: string) => keysApi.create(label),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['apikeys'] });
      setNewKeyValue(data.key);
      setNewKeyLabel('');
    },
  });

  const revokeKeyMutation = useMutation({
    mutationFn: (id: string) => keysApi.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['apikeys'] }),
  });

  const keys: ApiKeyInfo[] = keysData?.keys ?? [];
  const tabs = [
    { id: 'org' as const, label: 'Organizacao' },
    { id: 'account' as const, label: 'Conta' },
    { id: 'keys' as const, label: 'Chaves API' },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Configuracoes</h1>

      {success && <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-700 text-sm">{success}</div>}
      {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}

      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => { setTab(t.id); setError(''); setSuccess(''); }}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === t.id ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'org' && org && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-lg">
          <h2 className="font-semibold mb-4">Configuracoes da Organizacao</h2>
          <form onSubmit={(e) => {
            e.preventDefault();
            const updates: { name?: string; slug?: string } = {};
            if (orgName) updates.name = orgName;
            if (orgSlug) updates.slug = orgSlug;
            if (Object.keys(updates).length > 0) updateOrgMutation.mutate(updates);
          }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
              <input defaultValue={org.name} onChange={(e) => setOrgName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <input defaultValue={org.slug} onChange={(e) => setOrgSlug(e.target.value)} pattern="[a-z0-9]+(-[a-z0-9]+)*"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" />
              <p className="text-xs text-gray-400 mt-1">Apenas letras minusculas, numeros e hifens.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plano</label>
              <input value={org.plan.charAt(0).toUpperCase() + org.plan.slice(1)} disabled className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500" />
            </div>
            <button type="submit" disabled={updateOrgMutation.isPending}
              className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark disabled:opacity-50">
              {updateOrgMutation.isPending ? 'Salvando...' : 'Salvar'}
            </button>
          </form>
        </div>
      )}

      {tab === 'account' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-lg">
          <h2 className="font-semibold mb-4">Conta</h2>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
              <input value={user?.name ?? ''} disabled className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
              <input value={user?.email ?? ''} disabled className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Funcao</label>
              <input value={user?.orgRole ?? ''} disabled className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 capitalize" />
            </div>
          </div>
        </div>
      )}

      {tab === 'keys' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="font-semibold mb-3">Gerar Nova Chave API</h2>
            <div className="flex gap-3">
              <input value={newKeyLabel} onChange={(e) => setNewKeyLabel(e.target.value)} placeholder="Nome da chave (ex: Producao)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50" />
              <button onClick={() => { if (newKeyLabel) createKeyMutation.mutate(newKeyLabel); }}
                disabled={!newKeyLabel || createKeyMutation.isPending}
                className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark disabled:opacity-50">
                Gerar
              </button>
            </div>
            {newKeyValue && (
              <div className="mt-3 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                <p className="text-sm font-medium text-yellow-800 mb-1">Guarde esta chave com seguranca. Ela nao sera exibida novamente.</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm bg-white px-3 py-1.5 rounded border break-all">{newKeyValue}</code>
                  <button onClick={() => { navigator.clipboard.writeText(newKeyValue); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                    className="p-2 hover:bg-yellow-100 rounded">
                    {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="font-semibold">Chaves API ({keys.length})</h2>
            </div>
            {keys.length === 0 ? (
              <div className="p-8 text-center text-gray-400">Nenhuma chave API ainda.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {keys.map((key) => (
                  <div key={key.id} className="flex items-center justify-between px-5 py-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <Key className="h-4 w-4 text-gray-400" />
                        <span className="font-medium text-sm">{key.label}</span>
                        <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">{key.keyPrefix}...</code>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Criada em {formatDate(key.createdAt)}
                        {key.lastUsedAt && ` · Ultimo uso ${formatDateTime(key.lastUsedAt)}`}
                      </p>
                    </div>
                    <button onClick={() => { if (confirm('Revogar esta chave API?')) revokeKeyMutation.mutate(key.id); }}
                      className="p-1 text-gray-400 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
